import { useState, useCallback, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Lesson } from '@court-sync/shared';
import { lessonOverrideService } from '../services/lessonOverride';

export interface LowEnrollmentOccurrence {
  date: string;
  enrollment: number;
  minimumRequired: number;
}

export interface LowEnrollmentLessonAlert {
  lesson: Lesson;
  occurrences: LowEnrollmentOccurrence[];
}

export const useLowEnrollmentLessons = (academyId?: string) => {
  const [alerts, setAlerts] = useState<LowEnrollmentLessonAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchLowEnrollmentLessons = useCallback(async () => {
    if (!academyId) return;

    setIsLoading(true);
    try {
      const data = await lessonOverrideService.getLowEnrollmentLessons(academyId);
      if (isMountedRef.current) {
        setAlerts(data || []);
      }
    } catch (error) {
      console.error('Error fetching low enrollment lessons:', error);
      if (isMountedRef.current) {
        toast.error('Failed to load low enrollment data');
      }
      if (isMountedRef.current) {
        setAlerts([]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [academyId]);

  const cancelLesson = useCallback(
    async (
      lessonId: string,
      date: string,
      reason: string,
      notifyStudents: boolean,
      grantMakeupCredit: boolean = false
    ) => {
      if (!academyId) {
        throw new Error('Academy ID is required');
      }

      await lessonOverrideService.cancelLesson(
        lessonId,
        date,
        reason,
        notifyStudents,
        academyId,
        grantMakeupCredit
      );

      toast.success('Lesson cancelled and students notified');
      await fetchLowEnrollmentLessons();
    },
    [academyId, fetchLowEnrollmentLessons]
  );

  const postponeLesson = useCallback(
    async (
      lessonId: string,
      originalDate: string,
      newDate: string,
      newStartTime: string,
      newEndTime: string,
      reason: string,
      notifyStudents: boolean,
      notifySms: boolean = notifyStudents
    ) => {
      if (!academyId) {
        throw new Error('Academy ID is required');
      }

      await lessonOverrideService.postponeLesson(
        lessonId,
        originalDate,
        newDate,
        newStartTime,
        newEndTime,
        reason,
        notifyStudents,
        undefined, // newCourtId
        academyId,
        notifySms
      );

      toast.success('Lesson postponed and students notified');
      await fetchLowEnrollmentLessons();
    },
    [academyId, fetchLowEnrollmentLessons]
  );

  const cancelTermLesson = useCallback(
    async (
      lessonId: string,
      reason: string,
      notifyStudents: boolean,
      grantMakeupCredit: boolean = false
    ) => {
      if (!academyId) {
        throw new Error('Academy ID is required');
      }

      await lessonOverrideService.cancelTermLesson(
        lessonId,
        academyId,
        reason,
        undefined, // fromDate - defaults to today
        notifyStudents,
        grantMakeupCredit
      );

      toast.success('Term lesson cancelled. Students notified and refunds processed.');
      await fetchLowEnrollmentLessons();
    },
    [academyId, fetchLowEnrollmentLessons]
  );

  return {
    alerts,
    isLoading,
    fetchLowEnrollmentLessons,
    cancelLesson,
    postponeLesson,
    cancelTermLesson,
  };
};

export default useLowEnrollmentLessons;
